import { MAX_OCCURRENCES, type OccurrenceInput } from '@pulsify/shared/dto'
import type { OccurrenceForm } from '~/shared/types/event'

interface RecurrenceConfig {
  frequencyType: 'days' | 'weeks' | 'months'
  interval: number
  count: number
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function localDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function localTime(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function occurrenceFormToRequest(occurrence: OccurrenceForm): OccurrenceInput {
  const start = new Date(
    `${occurrence.date}T${occurrence.isAllDay ? '00:00' : occurrence.startTime || '00:00'}`,
  )
  const end = occurrence.isAllDay
    ? new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1)
    : new Date(`${occurrence.date}T${occurrence.endTime || '00:00'}`)
  if (!occurrence.isAllDay && end.getTime() <= start.getTime()) {
    end.setDate(end.getDate() + 1)
  }
  return {
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    isAllDay: occurrence.isAllDay,
  }
}

export function occurrenceFormsToRequests(occurrences: OccurrenceForm[]): OccurrenceInput[] {
  return occurrences.map(occurrenceFormToRequest)
}

export function occurrenceResponseToForm(occurrence: {
  startTime: string
  endTime: string
  isAllDay: boolean
}): OccurrenceForm {
  const start = new Date(occurrence.startTime)
  const end = new Date(occurrence.endTime)
  return {
    date: localDate(start),
    startTime: localTime(start),
    endTime: localTime(end),
    isAllDay: occurrence.isAllDay,
  }
}

/**
 * True when any dated occurrence is missing its time range - a non-all-day day
 * without both a start and end time. Drives the "more days" schedule validation.
 */
export function occurrencesMissingTime(occurrences: OccurrenceForm[]): boolean {
  return occurrences.some(
    (occurrence) => !occurrence.isAllDay && (!occurrence.startTime || !occurrence.endTime),
  )
}

export function makeDefaultOccurrence(afterDate?: string): OccurrenceForm {
  const base = afterDate ? new Date(`${afterDate}T00:00`) : new Date()
  if (afterDate) base.setDate(base.getDate() + 1)
  return { date: localDate(base), startTime: '18:00', endTime: '23:00', isAllDay: false }
}

export function daysInRange(fromDate: string, toDate: string): string[] {
  if (!fromDate) return []
  const start = new Date(`${fromDate}T00:00`)
  const end = toDate ? new Date(`${toDate}T00:00`) : start
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return []
  if (end.getTime() < start.getTime()) return [fromDate]
  const days: string[] = []
  const cursor = new Date(start)
  while (cursor.getTime() <= end.getTime() && days.length < MAX_OCCURRENCES) {
    days.push(localDate(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return days
}

/**
 * One occurrence per day in [from, to], each with the given default time range.
 * Times already edited for a date in `existing` are preserved so widening the
 * range doesn't discard fine-tuned rows.
 */
export function buildMultiDayOccurrences(
  fromDate: string,
  toDate: string,
  startTime: string,
  endTime: string,
  existing: OccurrenceForm[] = [],
): OccurrenceForm[] {
  const byDate = new Map(existing.map((occurrence) => [occurrence.date, occurrence]))
  return daysInRange(fromDate, toDate).map(
    (date) => byDate.get(date) ?? { date, startTime, endTime, isAllDay: false },
  )
}

/**
 * Repeats a base block of occurrences `count` times, offsetting each repetition's
 * dates by `interval` days/weeks/months. A one-day block yields N rows; an
 * M-day block yields M×N rows. Every output row keeps its own editable times.
 */
export function repeatOccurrences(
  block: OccurrenceForm[],
  config: RecurrenceConfig,
): OccurrenceForm[] {
  if (block.length === 0) return []
  const result: OccurrenceForm[] = []
  const seen = new Set<string>()
  for (let i = 0; i < config.count && result.length < MAX_OCCURRENCES; i++) {
    for (const occurrence of block) {
      const date = new Date(`${occurrence.date}T00:00`)
      if (config.frequencyType === 'months') {
        date.setMonth(date.getMonth() + i * config.interval)
      } else {
        const step = config.frequencyType === 'weeks' ? config.interval * 7 : config.interval
        date.setDate(date.getDate() + i * step)
      }
      const day = localDate(date)
      // Overlapping/short-interval repeats can land on the same start instant, which
      // the (eventId, startTime) unique index rejects - drop the collision here.
      const key = `${day}|${occurrence.isAllDay ? 'allday' : occurrence.startTime}`
      if (seen.has(key)) continue
      seen.add(key)
      result.push({ ...occurrence, date: day })
    }
  }
  return result.slice(0, MAX_OCCURRENCES)
}
